import React from 'react';

/**
 * Click-to-sort header cell + sorting helpers for the plain-<table> overview ledgers
 * (Discover landscape, Secure job results, Schema Intelligence profiled databases).
 * Click cycles ascending → descending → back to the page's default order (sort = null).
 */

/** Next sort state for a header click. null = return to the page's default order. */
export const nextSort = (prev, key) => {
  if (!prev || prev.key !== key) return { key, dir: 'asc' };
  if (prev.dir === 'asc') return { key, dir: 'desc' };
  return null;
};

/**
 * Stable sort of ledger rows by the active column. `accessors` maps sort key →
 * row → comparable value (string or number). Rows with no value always sink to
 * the bottom regardless of direction; strings compare case-insensitively with
 * numeric awareness ("run 2" < "run 10").
 */
export const sortRows = (rows, sort, accessors) => {
  if (!sort || typeof accessors[sort.key] !== 'function') return rows;
  const acc = accessors[sort.key];
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((rowA, rowB) => {
    const a = acc(rowA);
    const b = acc(rowB);
    const aEmpty = a == null || a === '';
    const bEmpty = b == null || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const cmp = typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    return cmp * dir;
  });
};

function SortableTh({ label, sortKey, sort, onSort, style }) {
  const active = !!(sort && sort.key === sortKey);
  return (
    <th
      role='columnheader'
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={`Sort by ${String(label).toLowerCase()}`}
      onClick={() => onSort(nextSort(sort, sortKey))}
      style={{
        padding: '8px 10px',
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {label}
      {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

export default SortableTh;
